'use client';

import React, { useEffect, useState, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { fetchWarehouseRevenue } from '@/lib/supabase/dashboardService';
import { createClient } from '@/lib/supabase/client';

interface TTProps {
  active?: boolean;
  payload?: Array<{ payload: { name: string; revenue: number; units: number } }>;
}

function CustomTooltip({ active, payload }: TTProps) {
  if (!active || !payload || payload.length === 0) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-card border border-border rounded-xl shadow-dropdown px-3 py-2 text-xs">
      <p className="font-600 text-foreground">{d.name}</p>
      <p className="text-muted-foreground">₹{(d.revenue / 100000).toFixed(2)}L revenue</p>
      <p className="text-muted-foreground">{d.units.toLocaleString('en-IN')} units</p>
    </div>
  );
}

const COLORS = ['var(--primary)', 'var(--accent)', '#8b5cf6', '#f97316'];

export default function WarehouseRevenueChart() {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(() => {
    fetchWarehouseRevenue(30).then((result) => {
      if (result) setData(result);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    loadData();

    const supabase = createClient();
    const channel = supabase
      .channel('warehouse-revenue-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'orders' }, loadData)
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [loadData]);

  if (loading) return <div className="h-[220px] bg-muted/30 rounded-lg animate-pulse" />;
  if (!data.length) return <div className="h-[220px] flex items-center justify-center text-xs text-muted-foreground">No warehouse data available</div>;

  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={data} margin={{ top: 4, right: 4, left: 0, bottom: 0 }} barSize={32}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
        <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} tickLine={false} axisLine={false} />
        <YAxis tickFormatter={(v) => `₹${(v / 100000).toFixed(0)}L`} tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }} tickLine={false} axisLine={false} width={52} />
        <Tooltip content={<CustomTooltip />} />
        <Bar dataKey="revenue" radius={[4, 4, 0, 0]}>
          {data.map((_, i) => <Cell key={`wh-cell-${i}`} fill={COLORS[i % COLORS.length]} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}